import { dinero, toDecimal } from "dinero.js";

import { getLocaleFormat, type LocaleFormat } from "./currencyFormatting";
import { Money } from "./money";

function extractNumbers(value: string): string {
  return value.replace(/\D/g, "");
}

function addFullDecimalZeros(value: string, precision: number): number {
  return parseInt(extractNumbers(value.padEnd(value.length + precision, "0")));
}

function stripThousandsSeparators(
  value: string,
  thousandsSeparator: string,
): string {
  if (thousandsSeparator === ",") {
    return value.replace(/,(?=\d{3}(?!\d))/g, "");
  } else if (thousandsSeparator === ".") {
    return value.replace(/\.(?=\d{3}(?!\d))/g, "");
  }
  return value;
}

function getDecimalSeparator(
  value: string,
  localeDecimalSeparator: string,
): string {
  const lastDotIndex = value.lastIndexOf(".");
  const lastCommaIndex = value.lastIndexOf(",");

  if (lastDotIndex !== -1 && lastCommaIndex !== -1) {
    return lastDotIndex > lastCommaIndex ? "." : ",";
  }

  if (lastDotIndex !== -1 || lastCommaIndex !== -1) {
    return localeDecimalSeparator;
  }

  return "";
}

function smallestUnitToDecimal(amount: number, currencyCode: string): number {
  if (!currencyCode) {
    return amount;
  }
  try {
    const currency = Money.getCurrencyInfo(currencyCode);
    const d = dinero({ amount, currency });
    return parseFloat(toDecimal(d));
  } catch (err) {
    throw Error("couldn't get currency exponent", { cause: err });
  }
}

export function renderMoneyWithoutCurrencySymbol(
  amount: number,
  currencyCode: string,
  locale: string,
): string {
  if (!currencyCode || amount === null || amount === undefined) {
    return String(amount ?? 0);
  }
  try {
    const value = smallestUnitToDecimal(amount, currencyCode);

    if (Number.isInteger(value)) {
      return new Intl.NumberFormat(locale, {
        style: "decimal",
        maximumFractionDigits: 0,
      }).format(value);
    }

    return new Intl.NumberFormat(locale, {
      style: "decimal",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return String(amount);
  }
}

// `formatMoneyInput` returns `amount`, in the smallest unit of `currencyCode`,
// as a decimal string in the major units of `currencyCode`, for use as the
// value of a money input. It's the inverse of `parseMoney`; the decimal
// separator of `locale` is used, and no thousands separators are added.
export function formatMoneyInput(
  amount: number,
  currencyCode: string,
  locale: string,
): string {
  const currency = Money.getCurrencyInfo(currencyCode);
  const { decimalSeparator } = getLocaleFormat(currencyCode, locale);
  return toDecimal(dinero({ amount, currency })).replace(".", decimalSeparator);
}

export function parseMoney(
  value: string,
  currencyCode: string,
  locale: string,
): number {
  let precision: number;
  let localeFormat: LocaleFormat;
  try {
    precision = Money.getCurrencyInfo(currencyCode).exponent;
    localeFormat = getLocaleFormat(currencyCode, locale);
  } catch (err) {
    throw Error("couldn't get currency info:" + err);
  }

  let cleanValue = value.trim();

  cleanValue = stripThousandsSeparators(
    cleanValue,
    localeFormat.thousandsSeparator,
  );

  const actualDecimalSeparator = getDecimalSeparator(
    cleanValue,
    localeFormat.decimalSeparator,
  );

  if (!actualDecimalSeparator) {
    return addFullDecimalZeros(cleanValue, precision);
  }

  const decimalIndex = cleanValue.lastIndexOf(actualDecimalSeparator);

  if (decimalIndex === -1) {
    return addFullDecimalZeros(cleanValue, precision);
  }

  const integerPart = cleanValue.substring(0, decimalIndex);
  let fractionalPart = cleanValue.substring(decimalIndex + 1);

  if (fractionalPart.length === 0) {
    return addFullDecimalZeros(integerPart, precision);
  }

  if (fractionalPart.length > precision) {
    fractionalPart = fractionalPart.substring(0, precision);
  }

  const paddedFractional = fractionalPart.padEnd(precision, "0");
  return parseInt(integerPart + paddedFractional);
}
